import React from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../../components/SEOMeta";
import styles from "../../styles/Button.module.css";

const CyberAttackCategories: React.FC = () => {
  // Define attack categories
  const categories = [
    {
      id: "network-based",
      title: "Network-Based Attacks",
      description: "Attacks that target network infrastructure and communication protocols"
    },
    {
      id: "web-application",
      title: "Web & Application Attacks",
      description: "Attacks targeting web applications, APIs, and application logic"
    },
    {
      id: "social-engineering",
      title: "Social Engineering & Deception",
      description: "Psychological manipulation and human-based attack techniques"
    },
    {
      id: "malware",
      title: "Malware & Malicious Software",
      description: "Malicious software and code-based attack vectors"
    },
    {
      id: "authentication",
      title: "Credential & Authentication Attacks",
      description: "Attacks targeting authentication systems and credentials"
    },
    {
      id: "physical",
      title: "Hardware & Physical Attacks",
      description: "Physical and hardware-based attack methods"
    },
    {
      id: "cryptographic",
      title: "Cryptographic & Data Attacks",
      description: "Attacks targeting encryption and data protection"
    },
    {
      id: "wireless-iot",
      title: "Wireless & IoT Attacks",
      description: "Attacks targeting wireless networks and IoT devices"
    },
    {
      id: "advanced",
      title: "Stealth & Advanced Attacks",
      description: "Sophisticated and stealthy attack techniques"
    }
  ];

  // Article structured data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Cyber Attack Categories: Complete Guide to Threat Classifications in 2026",
    description:
      "Complete guide to cyber attack categories and classifications. Explore network-based, web application, social engineering, malware, authentication, physical, cryptographic, wireless/IoT, and advanced attack types.",
    author: {
      "@type": "Organization",
      name: "CyberAnnals",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://cyberannals.com/blog/cyber-attack-categories",
    },
    publisher: {
      "@type": "Organization",
      name: "CyberAnnals",
      logo: {
        "@type": "ImageObject",
        url: "https://cyberannals.com/cyberAnnals_logo.png",
        width: 600,
        height: 60,
      },
    },
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      <SEOMeta
        title="Cyber Attack Categories: Complete Guide to Threat Classifications in 2026"
        description="Complete guide to cyber attack categories and classifications. Explore network-based, web application, social engineering, malware, authentication, physical, cryptographic, wireless/IoT, and advanced attack types."
        keywords="cyber attacks, attack categories, threat classifications, network attacks, web attacks, social engineering, malware, authentication attacks, physical attacks, cryptographic attacks, wireless attacks, advanced attacks"
        canonical="https://cyberannals.com/blog/cyber-attack-categories"
        ogType="article"
        structuredData={articleStructuredData}
      />

      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white mb-4">
            Cyber Attack Categories
          </h1>
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            Complete Guide to Threat Classifications in 2026
          </h2>
        </header>

        <div className="space-y-6 text-slate-700 dark:text-slate-300 leading-relaxed">
          <p className="text-lg">
            This comprehensive guide organizes all known cyber attack types into nine distinct categories.
            Select a category below to explore all attack types within that classification.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-white dark:bg-slate-800 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-slate-200 dark:border-slate-700 p-6"
              >
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  {category.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-300 mb-4">
                  {category.description}
                </p>
                <Link
                  to={`/blog/category/${category.id}`}
                  className="inline-flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium hover:underline"
                >
                  Explore Attacks
                  <svg className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>

        <footer className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
          <div className="flex flex-wrap gap-4 mb-6">
            <Link
              to="/blog"
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
            >
              ← More Blog Posts
            </Link>
            <Link
              to="/decades"
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
            >
              Explore Cyber Security History →
            </Link>
            <Link
              to="/toolkit"
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
            >
              Security Tools →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Popular Topics:
              </h3>
              <ul className="space-y-2">
                <li><Link to="/blog/what-is-cybersecurity" className={styles.link}>What is Cybersecurity?</Link></li>
                <li><Link to="/blog/what-is-firewall" className={styles.link}>What is a Firewall?</Link></li>
                <li><Link to="/blog/what-is-vpn" className={styles.link}>What is a VPN?</Link></li>
                <li><Link to="/blog/ai-driven-cyber-attacks" className={styles.link}>AI Driven Cyber Attacks</Link></li>
                <li><Link to="/blog/zero-trust-architecture" className={styles.link}>Zero Trust Architecture</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Cyber Attack Types:
              </h3>
              <ul className="space-y-2">
                <li><Link to="/blog/common-cyber-attacks" className={styles.link}>Common Cyber Attacks</Link></li>
                <li><Link to="/blog/most-popular-cyber-attacks" className={styles.link}>Most Popular Cyber Attacks</Link></li>
                <li><Link to="/blog/top-10-cyber-attacks" className={styles.link}>Top 10 Cyber Attacks</Link></li>
                <li><Link to="/blog/worst-cyber-attacks" className={styles.link}>Worst Cyber Attacks</Link></li>
                <li><Link to="/blog/every-type-of-cyber-attack" className={styles.link}>Every Type of Cyber Attack</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                Related Cyber Attacks:
              </h3>
              <div className="space-y-2">
                <div><Link to="/event/log4j-log4shell-2021" className={styles.link}>Log4j Log4Shell (2021)</Link></div>
                <div><Link to="/event/heartbleed" className={styles.link}>Heartbleed (2014)</Link></div>
                <div><Link to="/event/stuxnet" className={styles.link}>Stuxnet (2010)</Link></div>
                <div><Link to="/event/operation-aurora" className={styles.link}>Operation Aurora (2009)</Link></div>
                <div><Link to="/event/colonial-pipeline-ransomware" className={styles.link}>Colonial Pipeline Attack (2021)</Link></div>
                <div><Link to="/event/equifax-data-breach-2017" className={styles.link}>Equifax Data Breach (2017)</Link></div>
                <div><Link to="/event/bangladesh-bank-heist" className={styles.link}>Bangladesh Bank Heist (2016)</Link></div>
                <div><Link to="/event/anthem-breach" className={styles.link}>Anthem Breach (2015)</Link></div>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
            <div className="flex flex-wrap gap-4">
              <Link to="/sitemap.xml" className={styles.link}>Sitemap</Link>
              <Link to="/privacy-policy" className={styles.link}>Privacy Policy</Link>
              <Link to="/terms-of-service" className={styles.link}>Terms of Service</Link>
              <Link to="/contact" className={styles.link}>Contact Us</Link>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default CyberAttackCategories;